import React from 'react';
import { Flame, Clock, Users, AlertCircle } from 'lucide-react';

/**
 * Badges dinámicos para el modal de un entreno:
 * - 🔥 Casi lleno (>=80%)
 * - ⚡ Lleno
 * - ⏰ Empieza en Xh / mañana / pasado
 * - 🟢 Recién publicado
 * - 🦁 Tú + N compañeros
 */
export default function TrainingPulseBadges({ training, users, countMiembros, currentUserId }) {
  if (!training) return null;

  const total = countMiembros(training);
  const max = training.max_participantes || 0;
  const pct = max > 0 ? total / max : 0;

  // Estado de plazas
  let plazasBadge = null;
  if (max > 0) {
    if (pct >= 1) {
      plazasBadge = { icon: '⚡', text: 'Lleno', color: 'bg-red-900/30 text-red-300 border-red-700/50' };
    } else if (pct >= 0.8) {
      const restantes = max - total;
      plazasBadge = { icon: '🔥', text: `Casi lleno — ${restantes} plazas`, color: 'bg-orange-900/30 text-orange-300 border-orange-700/50' };
    } else if (pct >= 0.5) {
      plazasBadge = { icon: '🌟', text: 'Subiendo', color: 'bg-yellow-900/20 text-yellow-300 border-yellow-700/40' };
    }
  }

  // Tiempo restante
  let tiempoBadge = null;
  try {
    const tStart = new Date(`${training.fecha}T${training.hora || '00:00'}:00`);
    const ms = tStart.getTime() - Date.now();
    if (ms > 0) {
      const horas = ms / 3600000;
      if (horas < 1) {
        tiempoBadge = { icon: '⏰', text: `Empieza en ${Math.round(ms / 60000)} min`, color: 'bg-emerald-900/30 text-emerald-300 border-emerald-700/50' };
      } else if (horas < 6) {
        tiempoBadge = { icon: '⏰', text: `Empieza en ${Math.round(horas)}h`, color: 'bg-emerald-900/30 text-emerald-300 border-emerald-700/50' };
      } else if (horas < 24) {
        tiempoBadge = { icon: '🌅', text: 'Hoy', color: 'bg-emerald-900/30 text-emerald-300 border-emerald-700/50' };
      } else if (horas < 48) {
        tiempoBadge = { icon: '📅', text: 'Mañana', color: 'bg-blue-900/30 text-blue-300 border-blue-700/50' };
      }
    } else {
      const horasPasadas = Math.abs(ms) / 3600000;
      if (horasPasadas < 2) {
        tiempoBadge = { icon: '🟢', text: 'En marcha ahora', color: 'bg-emerald-900/40 text-emerald-300 border-emerald-700/60' };
      } else if (horasPasadas < 24) {
        tiempoBadge = { icon: '✅', text: 'Finalizado', color: 'bg-gray-800/50 text-gray-400 border-gray-700/50' };
      }
    }
  } catch (e) { /* no-op */ }

  // Tú + N compañeros
  let socialBadge = null;
  if (currentUserId && (training.participantes || []).includes(currentUserId)) {
    const otros = (training.participantes || []).filter(p => p !== currentUserId);
    if (otros.length === 0) {
      socialBadge = { icon: '🦁', text: 'Eres el primer lobo', color: 'bg-gold/15 text-gold border-gold/40' };
    } else if (otros.length === 1) {
      const compa = users[otros[0]];
      socialBadge = { icon: '🦁', text: compa ? `Tú + ${compa.nombre.split(' ')[0]}` : 'Tú + 1', color: 'bg-gold/15 text-gold border-gold/40' };
    } else {
      socialBadge = { icon: '🦁', text: `Tú + ${otros.length} lobos`, color: 'bg-gold/15 text-gold border-gold/40' };
    }
  }

  const badges = [plazasBadge, tiempoBadge, socialBadge].filter(Boolean);
  if (badges.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1.5" data-testid="training-pulse-badges">
      {badges.map((b, i) => (
        <span
          key={i}
          className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full border ${b.color}`}
        >
          <span>{b.icon}</span> {b.text}
        </span>
      ))}
    </div>
  );
}
